import {
    DropdownMenuProvider,
    DropdownRootProvider,
} from './DropdownContext/dropdownContext'

import React, {
    useEffect, useRef,
} from 'react'

import {DropdownState} from './types'
import {styled} from 'stitches/config'
import {styledDropdown} from './styles'

interface DropdownProps {
    children : React.ReactElement | React.ReactElement[]
    closeAfterSelection?: boolean
    closeOnScrollOutOfView?: boolean
    forceOpenState?: boolean
    onStateChange?: (isOpen: DropdownState) => void
}

const StyledDropdown = styled('div', styledDropdown)

const DropdownView: React.FC<DropdownProps> = (props: DropdownProps): React.ReactElement => {
    const {
        children,
        closeAfterSelection = true,
        closeOnScrollOutOfView = false,
        forceOpenState = false,
        onStateChange,
    } = props

    const defaultState: DropdownState = false
    const [isOpen, setIsOpen] = React.useState(defaultState)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const blurTimeoutRef = useRef<number | null>(null)
    const insideRef = useRef(false)
    const resetRef = useRef<number | null>(null)

    useEffect(() => {
        setIsOpen(forceOpenState)
        if (forceOpenState) {
            const dropdownButton = dropdownRef?.current?.querySelectorAll('[data-name=\'dropdown-button\']')[0] as HTMLElement
            if (dropdownButton) {
                setTimeout(() => {
                    dropdownButton.focus() // when forcing by default open state button need to receive focus
                }, 0)
            }
        }
    }, [forceOpenState])

    useEffect(() => () => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
        }

        if (resetRef.current) {
            window.cancelAnimationFrame(resetRef.current)
        }
    }, [])


    // Safari doesn't fire blur when clicking non-focusable elements
    // so we use a document-level capture listener to detect outside clicks.
    useEffect(() => {
        const handleOutsideInteraction = (event: Event) => {
            if (resetRef.current) {
                window.cancelAnimationFrame(resetRef.current)
                resetRef.current = null
            }

            const target = event?.target as Node | null
            const isInside = !!(target && dropdownRef.current?.contains(target))
            insideRef.current = isInside

            // Safari fix: If dropdown is open and click is outside, close immediately
            if (isOpen && !isInside) {
                setIsOpen(false)
                onStateChange?.(false)
            }

            resetRef.current = window.requestAnimationFrame(() => {
                insideRef.current = false
                resetRef.current = null
            })
        }

        document.addEventListener('pointerdown', handleOutsideInteraction, true)
        return () => {
            document.removeEventListener('pointerdown', handleOutsideInteraction, true)
        }
    }, [isOpen, onStateChange])

    useEffect(() => {
        if (!closeOnScrollOutOfView || !isOpen || !dropdownRef.current) {
            return undefined
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                // If less than 10% of the dropdown trigger is visible, close the dropdown
                if (!entry.isIntersecting || entry.intersectionRatio < 0.1) {
                    setIsOpen(false)
                    onStateChange?.(false)
                }
            },
            {threshold: [0, 0.1]},
        )

        observer.observe(dropdownRef.current)

        return () => {
            observer.disconnect()
        }
    }, [closeOnScrollOutOfView, isOpen, onStateChange])

    const onBlur = React.useCallback((event: React.FocusEvent<HTMLDivElement>) => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
        }

        const relatedTarget = event.relatedTarget as Node | null

        blurTimeoutRef.current = window.setTimeout(() => {
            if (insideRef.current) {
                blurTimeoutRef.current = null
                return
            }

            const {activeElement} = document ?? {}
            const nextTarget = relatedTarget ?? activeElement as Node | null

            if (nextTarget && dropdownRef.current?.contains(nextTarget)) {
                blurTimeoutRef.current = null
                return
            }

            const newState = false
            setIsOpen(newState)

            if (onStateChange) {
                onStateChange(newState)
            }
            blurTimeoutRef.current = null
        }, 0)
    }, [onStateChange])

    const onFocus = React.useCallback(() => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
            blurTimeoutRef.current = null
        }
    }, [])

    const onToggle = React.useCallback((): void => {
        setIsOpen((prev) => {
            const next = !prev
            onStateChange?.(next)
            return next
        })
    }, [onStateChange])

    const rootValue = React.useMemo(() => ({
        closeAfterSelection,
        dropdownRef,
    }), [closeAfterSelection, dropdownRef])

    const menuValue = React.useMemo(() => ({
        isOpen,
        onToggle,
    }), [isOpen])


    return (
        <DropdownRootProvider value={rootValue}>
            <DropdownMenuProvider value={menuValue}>
                <StyledDropdown
                    onBlur={onBlur}
                    onFocus={onFocus}
                    ref={dropdownRef}
                >
                    {children}
                </StyledDropdown>
            </DropdownMenuProvider>
        </DropdownRootProvider>
    )
}

DropdownView.defaultProps = {
    closeAfterSelection: true,
    closeOnScrollOutOfView: false,
    forceOpenState: false,
    onStateChange: undefined,
}

export default DropdownView
